'use client';

import { useShallow } from 'zustand/react/shallow';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from 'ui/components/Toast/use-toast';
import { useTranslation } from '@whoop/i18n/lang/client';
import type { Product } from 'services/generated/commerce-service/models/Product';
import { getPriceValues } from 'ui/utils/variantHelpers';
import {
  AmplitudeActions,
  AmplitudeEvents,
  AmplitudeNavigationTypes,
} from 'whoop-analytics/lib/types';
import { Button } from 'ui/components/ButtonV2/Button';
import { useRouter } from 'next/navigation';
import CollapsibleSection from './OplCollapsibleSection/OplCollapsibleSection';
import { useSiteStore } from '@/store/SiteStoreProvider';
import type {
  CollapsibleInfoSubsection,
  ContentfulDisclaimer,
  PageDisclaimer,
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';
import {
  DeviceSkuByTier,
  MembershipTier,
  TRIAL,
} from '@/lib/constants/maverick';
import { addToEcommCart } from '@/actions/cartActions';
import { useNavigation } from '@/hooks/navigation';
import {
  getAllMembershipVariants,
  getPeakTrialProduct,
  getSortedWhatsIncludedContent,
  getTierCompatibleProducts,
} from '@/lib/utils/maverickHelpers';
import ChooseMembershipSection from '@/components/OPLComponents/ChooseMembershipSection/ChooseMembershipSection';
import JoinNextGenerationSection from '@/components/OPLComponents/JoinNextGenerationSection/JoinNextGenerationSection';
import type { GenericItem } from '@/client/contentfulClient';
import MembershipComparison from '@/components/OPLComponents/MembershipComparison/membershipComparison';
import { WhatsIncludedCarousel } from '@/components/OPLComponents/WhatsIncluded/WhatsIncludedCarousel';
import type {
  MembershipTierInfo,
  WhatsIncludedTierInfo,
} from '@/types/maverickTypes';
import Warranty from '@/components/Warranty/Warranty';
import Disclaimers from '@/components/Disclaimers/Disclaimers';
import FAQ from '@/components/Faq/Faq';
import ChooseTrialSection from '@/components/OPLComponents/ChooseTrialSection/ChooseTrialSection';
import GenericContentBlock from '@/components/OPLComponents/GenericContentBlock/GenericContentBlock';
import StickyCta from '@/components/upgradeLandingPage/StickyCta/StickyCta';
import { useAnalytics } from '@/hooks/analytics';
import FamilyPlanSection from '@/components/OPLComponents/SaveMoreOnAFamilyPlanSection/FamilyPlanSection';
import { safePromise } from '@/lib/utils/promiseHelpers';
import { useExperimentService } from '@/hooks/experimentService';
import { EXPERIMENT_JF_TRIAL_BLOCK } from '@/lib/constants/experiments';
import { JF_VERTICAL_STACK } from '@/lib/constants/experiments';

interface JFOPLContentProps {
  joinTheNextGenContent?: GenericItem;
  trialNowChangeLaterContent?: GenericItem[];
  familyPlansContent?: GenericItem[];
  whatsIncludedContent?: WhatsIncludedTierInfo[];
  membershipTierFeatureLists?: MembershipTierComparisonFeatureList[];
  collapsibleSubsections?: CollapsibleInfoSubsection[];
  disclaimers?: ContentfulDisclaimer[];
  pageDisclaimers?: PageDisclaimer[];
  availableTiers: MembershipTierInfo[];
  trialMembership?: Product;
  memberships?: Product[];
  isMobile: boolean;
  children: React.ReactNode;
  trialBlockVariant: string | null;
  verticalStackVariant: string | null;
}

export default function JFOplContent({
  joinTheNextGenContent,
  trialNowChangeLaterContent,
  familyPlansContent,
  whatsIncludedContent,
  membershipTierFeatureLists,
  collapsibleSubsections,
  disclaimers,
  pageDisclaimers,
  availableTiers,
  memberships,
  trialMembership,
  isMobile,
  children,
  trialBlockVariant,
  verticalStackVariant,
}: JFOPLContentProps): JSX.Element {
  const {
    setEcommCart,
    cart,
    language,
    country,
    setSkipMemSelectFlow,
    isRafGiftFlow,
  } = useSiteStore(
    useShallow((state) => ({
      setEcommCart: state.setEcommCart,
      cart: state.cart,
      language: state.language,
      country: state.country,
      setSkipMemSelectFlow: state.setSkipMemSelectFlow,
      isRafGiftFlow: state.isRafGiftFlow,
    })),
  );

  const { goTo, preloadPage } = useNavigation();
  const router = useRouter();
  const { toast } = useToast();
  const { t: cartT } = useTranslation('cart');
  const { t } = useTranslation('membershipTiers');
  const { trackAmplitudeEvent } = useAnalytics();
  const { setVariant } = useExperimentService();
  const membershipSectionRef = useRef<HTMLDivElement>(null);
  const disclaimerSectionRef = useRef<HTMLDivElement>(null);

  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const availableTierSimple = availableTiers.map((tier) => tier.name);
  const whatsIncludedSorted = getSortedWhatsIncludedContent(
    whatsIncludedContent ?? [],
    availableTierSimple,
  );

  //Set initial tier to PEAK if available, otherwise set to last tier
  const [softSelectedTier, setSoftSelectedTier] = useState<MembershipTier>(
    availableTierSimple.includes(MembershipTier.PEAK)
      ? MembershipTier.PEAK
      : availableTierSimple[availableTierSimple.length - 1],
  );

  useEffect(() => {
    if (verticalStackVariant && isMobile) {
      setVariant(JF_VERTICAL_STACK, verticalStackVariant);
      if (verticalStackVariant === 'vertical-variant')
        setSoftSelectedTier(MembershipTier.LIFE);
    }
  }, [verticalStackVariant]);

  useEffect(() => {
    if (trialBlockVariant) {
      setVariant(EXPERIMENT_JF_TRIAL_BLOCK, trialBlockVariant);
    }
  }, [trialBlockVariant]);

  const setSoftSelectedTierWithTracking = (tier: MembershipTier) => {
    setSoftSelectedTier(tier);
    trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
      action: AmplitudeActions.SoftSelectChange,
      soft_select: tier,
    });
  };

  const trialRenewalPrice = useMemo(() => {
    const { annualMembership: peakAnnualMembership } = getAllMembershipVariants(
      getTierCompatibleProducts(memberships ?? [], MembershipTier.PEAK) ?? [],
    );
    return peakAnnualMembership
      ? getPriceValues(peakAnnualMembership).originalPrice
      : undefined;
  }, []);

  const handleTrialAddToCart = async () => {
    if (trialMembership) {
      const { data, error } = await safePromise(
        addToEcommCart({
          requestBody: {
            cart_id: cart.id,
            country,
            locale: language,
            sku: trialMembership.variants[0].sku,
            quantity: 1,
          },
        }),
      );
      if (error) {
        toast({
          title: cartT('errorTitle'),
          description: cartT('errorDescription'),
          variant: 'error',
        });
        setIsAddingToCart(false);
      } else if (data) {
        setEcommCart(data);
        setSkipMemSelectFlow(TRIAL);
        goTo(`bands?tier=${TRIAL}`);
      }
    }
  };

  const addToCart = async (
    tierName: MembershipTierWithTrial,
    product?: Product,
  ) => {
    if (isAddingToCart) {
      return;
    }
    // If a trial, add the trial to cart
    const isFreeTrial = Boolean(product && getPeakTrialProduct([product]));
    if (isFreeTrial && trialMembership) {
      await handleTrialAddToCart();
    } else {
      // Clear out skip mem select flow if not a trial (fixes a bug where users goes back to the landing page and is stuck in the trial skip mem select flow)
      setSkipMemSelectFlow('');
      // FUTURE TODO : is hardcoding the device sku okay? can we send something else to BE to add the device
      // If not a trial, add the tier-appropriate device to cart
      const { data, error } = await safePromise(
        addToEcommCart({
          requestBody: {
            cart_id: cart.id,
            country,
            locale: language,
            sku: DeviceSkuByTier[tierName],
            quantity: 1,
          },
        }),
      );

      if (error) {
        toast({
          title: cartT('errorTitle'),
          description: cartT('errorDescription'),
          variant: 'error',
        });
        setIsAddingToCart(false);
      } else if (data) {
        setEcommCart(data);
        goTo(`bands?tier=${tierName}`);
      }
    }
  };

  const handleAddToCart = async ({
    tierName,
    product,
    navigationType,
  }: {
    tierName: MembershipTierWithTrial;
    product?: Product;
    navigationType?: string;
  }) => {
    preloadPage(`/bands?tier=${tierName}`);
    setIsAddingToCart(true);
    await addToCart(tierName, product);
    trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
      action: AmplitudeActions.Navigation,
      navigation_type: navigationType,
      membership_type: tierName,
    });
  };

  const scrollToMembershipSection = () => {
    if (membershipSectionRef.current) {
      membershipSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const scrollToDisclaimerSection = () => {
    if (disclaimerSectionRef.current) {
      disclaimerSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <>
      {joinTheNextGenContent ? (
        <JoinNextGenerationSection
          content={joinTheNextGenContent}
          isTrialMembershipAvailable={Boolean(trialMembership)}
        />
      ) : null}
      <ChooseMembershipSection
        availableTiers={availableTiers}
        isAddingToCart={isAddingToCart}
        isMobile={isMobile}
        mcbCategory={null}
        onCardCTAClick={async (tierName) => {
          await handleAddToCart({
            tierName,
            navigationType: AmplitudeNavigationTypes.MembershipBlock,
          });
        }}
        onShowDisclaimers={scrollToDisclaimerSection}
        ref={membershipSectionRef}
        setSoftSelectedTier={setSoftSelectedTierWithTracking}
        softSelectedTier={softSelectedTier}
        title={t('chooseMembershipJoinToday')}
      />
      <div className='relative'>
        {trialMembership && trialRenewalPrice && !isRafGiftFlow ? (
          <ChooseTrialSection
            isAddingToCart={isAddingToCart}
            onCTAClick={async () => {
              await handleAddToCart({
                tierName: 'TRIAL',
                product: trialMembership,
                navigationType: AmplitudeNavigationTypes.TrialBlock,
              });
            }}
            renewalPrice={trialRenewalPrice}
            trialMembership={trialMembership}
          />
        ) : null}
        <div className='flex justify-center bg-black px-8 py-6 text-white md:py-14'>
          <div className='flex w-full max-w-md flex-col items-center rounded-lg text-center'>
            <h3 className='pb-2 text-2xl font-bold md:text-4xl'>
              {isRafGiftFlow ? t('stillOnWhoopHarvard') : t('alreadyAMember')}
            </h3>
            <p className='pb-4 font-medium md:text-xl'>
              {t('upgradeToTheNextGeneration')}
            </p>
            <Button
              className='text-xs hover:cursor-pointer'
              data-testid='upgrade-link'
              onClick={() => {
                trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
                  action: AmplitudeActions.Navigation,
                  navigation_type: AmplitudeNavigationTypes.UpgradeBlock,
                });
                router.refresh();
                goTo('/upgrade');
              }}
              variant='primary'
            >
              {t('upgradeToday')}
            </Button>
          </div>
        </div>
        {children}
        {membershipTierFeatureLists && membershipTierFeatureLists.length > 0 ? (
          <MembershipComparison
            availableTiers={availableTiers}
            disclaimers={disclaimers}
            isAddingToCart={isAddingToCart}
            isUpgrade={false}
            membershipTierFeatureLists={membershipTierFeatureLists}
            onSelect={async (tierName, product) => {
              await handleAddToCart({
                tierName,
                product,
                navigationType: AmplitudeNavigationTypes.CompareBlock,
              });
            }}
            onShowDisclaimers={scrollToDisclaimerSection}
            softSelectedTier={softSelectedTier}
            trialMembership={isRafGiftFlow ? undefined : trialMembership}
          />
        ) : null}
        {whatsIncludedSorted.length > 0 ? (
          <WhatsIncludedCarousel
            softSelectedTier={softSelectedTier}
            whatsIncludedInfo={whatsIncludedSorted}
          />
        ) : null}
        {familyPlansContent && !isRafGiftFlow ? (
          <FamilyPlanSection familyPlansContent={familyPlansContent?.[0]} />
        ) : null}
        {collapsibleSubsections && collapsibleSubsections.length > 0 ? (
          <CollapsibleSection collapsibleSubsections={collapsibleSubsections} />
        ) : null}
        {trialMembership && trialNowChangeLaterContent && !isRafGiftFlow ? (
          <GenericContentBlock
            content={trialNowChangeLaterContent}
            ctaText={t('startYourFreeTrial')}
            isAddingToCart={isAddingToCart}
            onCTAClick={async () => {
              handleAddToCart({
                tierName: TRIAL,
                product: trialMembership,
                navigationType: AmplitudeNavigationTypes.SwitchingBlock,
              });
            }}
          />
        ) : null}
        <FAQ isTrialAvailable={Boolean(trialMembership)} />
        <Warranty />
        {pageDisclaimers ? (
          <Disclaimers
            pageDisclaimers={pageDisclaimers}
            ref={disclaimerSectionRef}
          />
        ) : null}
        <StickyCta
          onChangeClick={scrollToMembershipSection}
          onContinueClick={() => {
            handleAddToCart({
              tierName: softSelectedTier,
              product: availableTiers.find(
                (tier) => tier.name === softSelectedTier,
              )?.membershipProduct,
              navigationType: AmplitudeNavigationTypes.StickyCta,
            });
          }}
          softSelectedTier={softSelectedTier}
        />
      </div>
    </>
  );
}
